import React, { useEffect } from 'react';

import {
  useAppDispatch,
  useAppSelector,
  useTranslate,
  useWidgetsTheme as theme,
  useWidgetParam,
} from '@src/core/hooks';
import {
  completeCartSelector,
  cartSetCouponErrorsSelector,
  openCartModal,
  cartStateSelector,
} from '@src/features/cart';
import { confirmCartOrder } from '@src/features/order';
import { DefaultMessagesKeys } from '@src/i18n/useTranslate';
import styled from 'styled-components';
import { LayoutColors } from '@src/core/theme';
import { Spinner } from '@src/components/reusable';
import { changeLocation } from '@src/core/store';
import { checkoutErrorsSelector } from '@src/features/checkout';

export const SaleConfirmation = () => {
  const dispatch = useAppDispatch();
  const { isLoading, isReady, hasErrors } = useAppSelector(completeCartSelector);
  const couponErrors = useAppSelector(cartSetCouponErrorsSelector);
  const checkoutErrors = useAppSelector(checkoutErrorsSelector);
  const { i18nElement, i18n } = useTranslate();
  const actionColor = theme('actionColor');
  const { isModalOpened } = useAppSelector(cartStateSelector);

  const accountPage = useWidgetParam('account-page');
  const redirect = () => dispatch(changeLocation(accountPage ?? ''));

  useEffect(() => {
    if (!isLoading) {
      if (hasErrors && !isModalOpened) {
        dispatch(openCartModal());
      } else if (isReady) {
        dispatch(confirmCartOrder());
      } else if (!couponErrors) {
        redirect();
      }
    }
  }, [isLoading, isReady, couponErrors, hasErrors]);

  if (isLoading) {
    return (
      <SpinnerWrapper>
        <Spinner visible spinnerColor={actionColor} />
      </SpinnerWrapper>
    );
  }

  if (couponErrors) {
    return (
      <ErrorMessage>
        {couponErrors.map((key) => (
          <span key={key}>{i18n(key as DefaultMessagesKeys)}</span>
        ))}
      </ErrorMessage>
    );
  }

  if (checkoutErrors?.length) {
    return (
      <ErrorMessage>
        {checkoutErrors.map((key) => (
          <span key={key}>{i18n(key as DefaultMessagesKeys)}</span>
        ))}
      </ErrorMessage>
    );
  }

  if (!isLoading && !isReady) {
    return (
      <SpinnerWrapper>
        <Spinner visible spinnerColor={actionColor} />
      </SpinnerWrapper>
    );
  }

  return isReady ? (
    <div>{i18nElement('checkout_confirmingYourPurchase')}</div>
  ) : (
    <div>{i18nElement('checkout_yourCartIsEmpty')}</div>
  );
};

const ErrorMessage = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${LayoutColors.errorDark} !important;

  span {
    text-align: center;
  }
`;

const SpinnerWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto;
`;

export default SaleConfirmation;
